import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LayoutDashboard, Building2, CreditCard, Users, MessageSquare,
  Settings, BarChart3, Shield, LogOut, Menu, X, ChevronRight, Eye, Lock, Activity, Plug
} from 'lucide-react';

const NAV_ITEMS = [
  { key: 'dashboard', labelKey: 'saLayout.nav_dashboard', label: 'Tableau de bord', icon: LayoutDashboard, path: '/super-admin' },
  { key: 'analytics', labelKey: 'saLayout.nav_analytics', label: 'Analytics', icon: Activity, path: '/super-admin/analytics' },
  { key: 'companies', labelKey: 'saLayout.nav_companies', label: 'Entreprises', icon: Building2, path: '/super-admin/companies' },
  { key: 'subscriptions', labelKey: 'saLayout.nav_subscriptions', label: 'Abonnements', icon: CreditCard, path: '/super-admin/subscriptions' },
  { key: 'users', labelKey: 'saLayout.nav_users', label: 'Utilisateurs', icon: Users, path: '/super-admin/users' },
  { key: 'stats', labelKey: 'saLayout.nav_stats', label: 'Statistiques', icon: BarChart3, path: '/super-admin/stats' },
  { key: 'communications', labelKey: 'saLayout.nav_communications', label: 'Communications', icon: MessageSquare, path: '/super-admin/communications' },
  { key: 'impersonation', labelKey: 'saLayout.nav_impersonation', label: 'Mode Support', icon: Eye, path: '/super-admin/impersonation' },
  { key: 'config', labelKey: 'saLayout.nav_config', label: 'Configuration', icon: Settings, path: '/super-admin/config' },
  { key: 'integrations', labelKey: 'saLayout.nav_integrations', label: 'Intégrations API', icon: Plug, path: '/super-admin/integrations' },
  { key: 'security', labelKey: 'saLayout.nav_security', label: 'Sécurité (2FA)', icon: Lock, path: '/super-admin/security' },
  { key: 'logs', labelKey: 'saLayout.nav_logs', label: 'Journal & Audit', icon: Shield, path: '/super-admin/logs' },
];

export const SuperAdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[var(--rm-bg-base)] text-gray-100 flex" data-testid="super-admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0d1321] border-r border-white/5 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Brand */}
          <div className="px-5 py-5 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#009b3a] to-[#fedd00] flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#0a0f1c]" />
              </div>
              <div>
                <h1 className="text-sm font-bold tracking-wide text-white">GUYAREPAIR</h1>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest">{t('saLayout.super_admin')}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {NAV_ITEMS.map(item => {
              const isActive = currentPath === item.path || (item.path !== '/super-admin' && currentPath.startsWith(item.path));
              const isExactDashboard = item.key === 'dashboard' && currentPath === '/super-admin';
              const active = isExactDashboard || isActive;

              return (
                <button
                  key={item.key}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  data-testid={`nav-${item.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all group ${
                    active
                      ? 'bg-[#009b3a]/15 text-[#00d946] border border-[#009b3a]/20'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'
                  }`}
                >
                  <item.icon className={`w-4 h-4 ${active ? 'text-[#00d946]' : 'text-gray-500 group-hover:text-gray-300'}`} />
                  <span>{t(item.labelKey)}</span>
                  {active && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-50" />}
                </button>
              );
            })}
          </nav>

          {/* User / Logout */}
          <div className="px-3 py-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#009b3a] to-[#fedd00] flex items-center justify-center text-xs font-bold text-[#0a0f1c]">
                SA
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.email || user?.username}</p>
                <p className="text-[10px] text-gray-500">{t('saLayout.role_super_admin')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('saLayout.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-white/5 bg-[#0d1321]/80 backdrop-blur-md flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-3 p-1.5 rounded-lg hover:bg-white/5"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <Eye className="w-3.5 h-3.5" />
            <span>{t('saLayout.top_bar_title')}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
